import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { AnimatePresence } from 'motion/react';
import { ActionCard } from './cards/ActionCard';
import { RejectionModal } from './rejection';
import { AcceptModal } from './acceptance';
import { EditModal } from './edit';
import { useParams } from 'react-router-dom';
import { SwipeableCardHandle } from './cards/base/SwipeableCard';
import { usePaginatedActions, useAcceptAction, useRejectAction } from '../store/hooks';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  openRejectionModal,
  openAcceptModal,
  openEditModal,
  AcceptModalContext,
  clearPendingCommand,
} from '../store/slice';
import {
  RejectionModalContext,
  EditModalContext,
  ActionCommand,
  ActionCommandType,
} from '../types';
import { ActionState, ActionType } from '../constants';
import {
  isCommandAllowed,
  requiresCommandConfirmation,
  getActionTypeDefinition,
} from '../registry/actionTypeRegistry';
import { CardManagementProvider } from '../contexts/CardManagementContext';
import { useOnboardingSteps, useUserState } from '@features/user';
import { useModal } from '@store/hooks';
import { useTracking } from '../../../context/TrackingContext';
import { EventType, InteractionType } from '../../../types/events';

export const Actions = () => {
  const { noteId } = useParams<{ noteId: string }>();
  const dispatch = useAppDispatch();
  const onboardingSteps = useOnboardingSteps();
  const userState = useUserState();
  const { open: openModal } = useModal();
  const { trackEvent } = useTracking();
  const pendingCommand = useAppSelector(state => state['actions/ui']?.pendingCommand);

  // Ref to track card refs by action ID
  const cardRefsMap = useRef<Map<string, SwipeableCardHandle>>(new Map());

  // States for polling control
  const [isPollingEnabled, setIsPollingEnabled] = useState(true);
  const [pollingInterval, setPollingInterval] = useState(30000); // Default: 30 seconds

  // Effect to disable polling when document/tab is not visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPollingEnabled(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initialize polling state
    setIsPollingEnabled(!document.hidden);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Use usePaginatedActions with dynamic polling interval
  const { data: actions, isLoading } = usePaginatedActions(
    noteId ? 'note' : 'dashboard',
    // 'dashboard',
    noteId,
    100,
    isPollingEnabled ? pollingInterval : 0,
    true, // Refetch on focus
  );

  // Use action mutation hooks
  const acceptAction = useAcceptAction();
  const rejectAction = useRejectAction();

  // Functions to manage card refs
  const registerCardRef = useCallback((actionId: string, ref: SwipeableCardHandle) => {
    cardRefsMap.current.set(actionId, ref);
  }, []);

  const unregisterCardRef = useCallback((actionId: string) => {
    cardRefsMap.current.delete(actionId);
  }, []);

  const resetCard = useCallback((actionId: string) => {
    const cardRef = cardRefsMap.current.get(actionId);
    if (cardRef && cardRef.resetCard) {
      cardRef.resetCard();
    }
  }, []);

  const animateReject = useCallback(
    (
      actionId: string,
      rejectionData?: {
        rejection_reason?: string;
        rejection_payload?: Record<string, any>;
      },
    ) => {
      const cardRef = cardRefsMap.current.get(actionId);
      if (cardRef && cardRef.animateReject) {
        cardRef.animateReject(() => {
          rejectAction.mutate(actionId, rejectionData);
        });
      } else {
        // Fallback: call API directly if card ref not available
        rejectAction.mutate(actionId, rejectionData);
      }
    },
    [rejectAction],
  );

  const appointments = actions || [];

  const animateAccept = useCallback(
    (actionId: string, updatedPayload?: any) => {
      const cardRef = cardRefsMap.current.get(actionId);
      const prevPayload = appointments.find(a => a.action_id === actionId)?.action_payload;
      const payload = {
        action_payload: {
          ...prevPayload,
          ...updatedPayload,
        },
      };

      if (cardRef && cardRef.animateAccept) {
        cardRef.animateAccept(() => {
          // Call accept action API after animation
          acceptAction.mutate({
            actionId,
            payload,
          });
        });
      } else {
        // Fallback: call API directly if card ref not available
        acceptAction.mutate({
          actionId,
          payload,
        });
      }
    },
    [acceptAction, appointments],
  );

  // Adjust polling interval based on number of appointments
  useEffect(() => {
    if (appointments.length === 0) {
      setPollingInterval(10000); // 10 seconds
    } else {
      setPollingInterval(30000); // 30 seconds
    }
  }, [appointments.length]);

  // New command handler system
  const handleCommand = useCallback(
    (actionId: string, command: ActionCommand) => {
      const appointment = appointments.find(a => a.action_id === actionId);

      if (!appointment) {
        console.warn(`Action not found: ${actionId}`);
        return;
      }

      const actionType = appointment.action_type as ActionType;
      const actionDefinition = getActionTypeDefinition(actionType);
      const config = actionDefinition?.config;

      // Check if command is allowed for this action's current state
      if (!isCommandAllowed(actionType, command.type, appointment.state)) {
        console.warn(
          `Command ${command.type} not allowed for action ${actionId} in state ${appointment.state}`,
        );
        return;
      }

      // Check onboarding for accept-like commands
      if (
        config?.requiresOnboarding &&
        (command.type === ActionCommandType.ACCEPT ||
          command.type === ActionCommandType.ACCEPT_WITH_EDITS ||
          command.type === ActionCommandType.SAVE_EDIT)
      ) {
        // Get the current onboarding index from command metadata, or start at 0
        const currentOnboardingIndex = command.metadata?.onboardingIndex || 0;

        // Find the first incomplete onboarding step starting from current index
        for (let i = currentOnboardingIndex; i < config.requiresOnboarding.length; i++) {
          const onboardingStep = config.requiresOnboarding[i];

          // Check completion status using either onboardingKey or checkFunction
          const isCompleted = (() => {
            if (onboardingStep.onboardingKey) {
              // Legacy check using onboardingKey
              return (
                onboardingSteps?.[onboardingStep.onboardingKey as keyof typeof onboardingSteps] ===
                true
              );
            }

            if (onboardingStep.checkFunction) {
              // Dynamic check using checkFunction
              return onboardingStep.checkFunction({
                onboardingSteps: onboardingSteps || {},
                userState,
                action: appointment,
              });
            }

            // If neither is specified, consider it incomplete
            return false;
          })();

          if (!isCompleted) {
            // Reset the card position since onboarding is required
            resetCard(actionId);

            // Open onboarding modal with continuation
            openModal(onboardingStep.modalType, {
              continuationAction: {
                type: 'actions/ui/setPendingCommand',
                payload: {
                  actionId,
                  command: {
                    ...command,
                    metadata: {
                      ...command.metadata,
                      onboardingIndex: i + 1, // Continue from next step after this one completes
                    },
                  },
                },
              },
            });
            return; // Stop here, onboarding will resume action
          }
        }
      }

      // Check validation for accept-like commands
      if (
        config?.validation &&
        (command.type === ActionCommandType.ACCEPT ||
          command.type === ActionCommandType.ACCEPT_WITH_EDITS ||
          command.type === ActionCommandType.SAVE_EDIT)
      ) {
        // Check if we have pre-validated patient data in command metadata
        const preValidatedData = command.metadata?.validatedPatientData;

        if (preValidatedData) {
          // We have pre-validated data from the PatientInfoCollectionModal
          // Trust this data and skip validation
          console.log('Using pre-validated patient data from modal', preValidatedData);
        } else {
          // Normal validation flow
          const validationResult = config.validation.validate(appointment);

          if (!validationResult.isValid) {
            // Reset the card position since validation failed
            resetCard(actionId);

            // Open validation modal if configured
            if (config.validation.validationModal) {
              openModal(config.validation.validationModal, {
                action: appointment,
                missingFields: validationResult.missingFields,
                errorMessage: validationResult.errorMessage,
                continuationAction: {
                  type: 'actions/ui/setPendingCommand',
                  payload: {
                    actionId,
                    command,
                  },
                },
              });
            }
            return; // Stop here, validation modal will resume action
          }
        }
      }

      console.log(`🎯 Processing command ${command.type} for action ${actionId}:`, command);

      // Handle different command types
      switch (command.type) {
        case ActionCommandType.ACCEPT:
          // Track accept interaction
          trackEvent(EventType.ACTION_INTERACTED, {
            action_id: actionId,
            action_type: appointment.action_type,
            interaction_type: InteractionType.ACCEPT,
            encounter_id: appointment.encounter_id || null,
            patient_id: appointment.patient?.patient_id || null,
          });

          if (requiresCommandConfirmation(actionType, command.type)) {
            // Open accept modal for confirmation
            const modalContext: AcceptModalContext = {
              action: appointment,
              actionType: actionType,
              patientName: appointment.patient?.name || undefined,
            };
            dispatch(openAcceptModal(modalContext));
          } else {
            // Direct acceptance with animation
            animateAccept(actionId, appointment.action_payload);
          }
          break;

        case ActionCommandType.ACCEPT_WITH_EDITS:
          // Track accept with edits interaction
          trackEvent(EventType.ACTION_INTERACTED, {
            action_id: actionId,
            action_type: appointment.action_type,
            interaction_type: InteractionType.ACCEPT,
            encounter_id: appointment.encounter_id || null,
            patient_id: appointment.patient?.patient_id || null,
          });

          if (requiresCommandConfirmation(actionType, command.type)) {
            // Create action with updated payload for modal
            const updatedAction = {
              ...appointment,
              action_payload: {
                ...appointment.action_payload,
                ...command.payload,
              },
            };

            const modalContext: AcceptModalContext = {
              action: updatedAction,
              actionType: actionType,
              patientName: appointment.patient?.name || undefined,
            };
            dispatch(openAcceptModal(modalContext));
          } else {
            // Direct acceptance with edits and animation
            const updatedPayload = {
              ...appointment.action_payload,
              ...command.payload,
            };

            animateAccept(actionId, updatedPayload);
          }
          break;

        case ActionCommandType.REJECT:
          // Track reject interaction
          trackEvent(EventType.ACTION_INTERACTED, {
            action_id: actionId,
            action_type: appointment.action_type,
            interaction_type: InteractionType.REJECT,
            encounter_id: appointment.encounter_id || null,
            patient_id: appointment.patient?.patient_id || null,
          });

          if (requiresCommandConfirmation(actionType, command.type)) {
            // Open rejection modal
            const modalContext: RejectionModalContext = {
              action: appointment,
              actionType: actionType,
              patientName: appointment.patient?.name || undefined,
            };
            dispatch(openRejectionModal(modalContext));
          } else {
            // Direct rejection - for now, just pass the basic rejection without payload
            // If needed in the future, we can enhance this to include rejection data
            rejectAction.mutate(actionId);
          }
          break;

        case ActionCommandType.EXPAND:
          // Track expand interaction
          trackEvent(EventType.ACTION_INTERACTED, {
            action_id: actionId,
            action_type: appointment.action_type,
            interaction_type: InteractionType.EXPAND,
            encounter_id: appointment.encounter_id || null,
            patient_id: appointment.patient?.patient_id || null,
          });

          // Handle expand through store/context - placeholder for now
          console.log(`📖 Expand action ${actionId}`);
          break;

        case ActionCommandType.COLLAPSE:
          // Track collapse interaction
          trackEvent(EventType.ACTION_INTERACTED, {
            action_id: actionId,
            action_type: appointment.action_type,
            interaction_type: InteractionType.COLLAPSE,
            encounter_id: appointment.encounter_id || null,
            patient_id: appointment.patient?.patient_id || null,
          });

          // Handle collapse through store/context - placeholder for now
          console.log(`📕 Collapse action ${actionId}`);
          break;

        case ActionCommandType.EDIT:
          // Cards handle their own edit state, just log for now
          console.log(`📝 Edit mode activated for action ${actionId}`);
          break;

        case ActionCommandType.EDIT_IN_MODAL:
          // Track expand interaction
          trackEvent(EventType.ACTION_INTERACTED, {
            action_id: actionId,
            action_type: appointment.action_type,
            interaction_type: InteractionType.EXPAND,
            encounter_id: appointment.encounter_id || null,
            patient_id: appointment.patient?.patient_id || null,
          });
          // Open edit modal
          const editContext: EditModalContext = {
            action: appointment,
            actionType: actionType,
            patientName: appointment.patient?.name || undefined,
          };
          dispatch(openEditModal(editContext));
          break;

        case ActionCommandType.SAVE_EDIT:
          // Track save edit interaction (this is a form of accept)
          trackEvent(EventType.ACTION_INTERACTED, {
            action_id: actionId,
            action_type: appointment.action_type,
            interaction_type: InteractionType.ACCEPT,
            encounter_id: appointment.encounter_id || null,
            patient_id: appointment.patient?.patient_id || null,
          });

          // Update the action with edited data
          if (command.payload) {
            // Use animation for consistency
            const updatedPayload = {
              ...appointment.action_payload,
              ...command.payload,
            };
            animateAccept(appointment.action_id, updatedPayload);
          }
          break;

        case ActionCommandType.CANCEL_EDIT:
          // Cards handle their own edit state, just log for now
          console.log(`❌ Edit mode cancelled for action ${actionId}`);
          break;

        default:
          console.warn(`Unknown command type: ${command.type} for action ${actionId}`);
          break;
      }
    },
    [appointments, dispatch, acceptAction, rejectAction, onboardingSteps, openModal, trackEvent],
  );

  // Process pending commands from modals
  useEffect(() => {
    if (pendingCommand) {
      handleCommand(pendingCommand.actionId, pendingCommand.command);
      dispatch(clearPendingCommand());
    }
  }, [pendingCommand, handleCommand, dispatch]);

  return (
    <CardManagementProvider
      resetCard={resetCard}
      animateReject={animateReject}
      animateAccept={animateAccept}
    >
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          position: 'relative',
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: 'text.disabled',
              fontSize: '0.75rem',
              fontWeight: 500,
            }}
          >
            {appointments.length === 0 && !isLoading
              ? 'No suggested actions right now'
              : 'Suggested Actions'}
          </Typography>
        </Box>
        {appointments.length > 0 && <Divider sx={{ marginY: 2, width: '100%' }} />}
        <AnimatePresence mode="popLayout">
          {appointments
            // Filter out appointments that are already ACCEPTED or REJECTED
            // They'll be removed immediately due to optimistic updates
            .filter(
              appointment =>
                appointment.state !== ActionState.ACCEPTED &&
                appointment.state !== ActionState.REJECTED,
            )
            .map((appointment, index) => (
              <ActionCard
                key={appointment.action_id}
                action={appointment}
                onCommand={command => handleCommand(appointment.action_id, command)}
                customIndex={index} // Pass index for staggered animations
                onRegisterCardRef={registerCardRef}
                onUnregisterCardRef={unregisterCardRef}
              />
            ))}
        </AnimatePresence>

        {/* Rejection Modal */}
        <RejectionModal />

        {/* Accept Modal */}
        <AcceptModal />

        {/* Edit Modal */}
        <EditModal />
      </Box>
    </CardManagementProvider>
  );
};
